import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_ROOM_FEET,
  DEFAULT_WALL_LABELS,
  ROOM,
  ROOM_FEET_LIMITS,
  ROOM_ITEM_KINDS,
  ROOM_ITEM_SPECS,
  clampRoomFeet,
  clampToRoom,
  findViolations,
  isSeat,
  itemLabel,
  planSeating,
  roomSizeLabel,
  roomUnits,
  snap,
  squareFeetPerStudent,
  studentName,
  type RoomItem,
  type RoomItemKind,
  type RuleViolation,
  type Student,
  type Wall,
} from '@td/shared';
import { useResetChart, useSaveChart, useSeatingChart } from '../hooks/useSeating';
import { useStudents } from '../hooks/useStudents';
import { RoomCanvas } from '../components/RoomCanvas';
import { StudentAvatar } from '../components/StudentAvatar';
import { usePrivacy } from '../privacy/PrivacyContext';
import { useConfirm } from '../components/ConfirmDialog';
import { CollapsiblePanel } from '../components/CollapsiblePanel';
import { SeatingRules } from '../components/SeatingRules';
import { RuleAlert, RuleBanner, useRuleSound } from '../components/RuleAlert';
import { useSeatingRules } from '../hooks/useSeatingRules';

const GROUP_LABELS: Record<string, string> = {
  seating: 'Seating',
  fixtures: 'Walls and fixtures',
  furniture: 'Furniture',
};

export function SeatingPage() {
  const { data: chart, isLoading, error } = useSeatingChart();
  const { data: students = [] } = useStudents();
  const { data: rules = [] } = useSeatingRules();
  const save = useSaveChart();
  const reset = useResetChart();
  const confirm = useConfirm();
  const { privacy, toggle } = usePrivacy();

  const [items, setItems] = useState<RoomItem[] | null>(null);
  const [wallLabels, setWallLabels] = useState<Record<Wall, string> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [locked, setLocked] = useState<boolean | null>(null);
  const [dirty, setDirty] = useState(false);
  const [overTray, setOverTray] = useState(false);
  const [feet, setFeet] = useState<{ width: number; height: number } | null>(null);
  const [highlight, setHighlight] = useState<string[] | null>(null);
  const [alerting, setAlerting] = useState<RuleViolation[] | null>(null);
  // Zoom is per device, not per chart: how big the room needs to be depends on
  // the screen and the eyes in front of it, which is exactly what a staffroom
  // full of teachers does not agree on.
  const [zoom, setZoom] = useState(() => {
    const raw = Number(localStorage.getItem('td.seatingZoom'));
    return Number.isFinite(raw) && raw >= 1 ? Math.min(raw, 3) : 1;
  });
  // The arrangement before the last seating change, so a rule-breaking move
  // can be put straight back.
  const undoRef = useRef<RoomItem[] | null>(null);
  const sound = useRuleSound();
  const saveTimer = useRef<number | null>(null);

  // The server's copy seeds the canvas once; after that the canvas leads and
  // pushes changes down, so a save landing mid-drag can't yank a desk back.
  useEffect(() => {
    if (chart && items === null) {
      setItems(chart.items);
      setWallLabels(chart.wallLabels);
      setFeet({ width: chart.widthFt, height: chart.heightFt });
      setLocked(chart.locked);
    }
  }, [chart, items]);

  const roster = useMemo(() => students.filter((s) => s.active), [students]);
  const seated = useMemo(
    () => new Set((items ?? []).map((i) => i.studentId).filter(Boolean) as string[]),
    [items],
  );
  const unseated = roster.filter((s) => !seated.has(s.id));

  const nameOfRef = useRef((id: string) => id);
  nameOfRef.current = (id: string) => {
    const s = roster.find((x) => x.id === id);
    return s ? studentName(s) : 'a student who has left';
  };
  const selected = (items ?? []).find((i) => i.id === selectedId) ?? null;

  const size = feet ?? DEFAULT_ROOM_FEET;
  const room = useMemo(() => roomUnits(size.width, size.height), [size.width, size.height]);

  /** Every mutation goes through here so nothing forgets to schedule a save. */
  function apply(
    next: RoomItem[],
    labels?: Record<Wall, string>,
    dims?: { width: number; height: number },
    lock?: boolean,
  ) {
    setItems(next);
    if (labels) setWallLabels(labels);
    if (dims) setFeet(dims);
    if (lock !== undefined) setLocked(lock);
    setDirty(true);

    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    // Dragging fires continuously; waiting for a pause turns a hundred
    // positions into one write.
    saveTimer.current = window.setTimeout(() => {
      const at = dims ?? size;
      save.mutate(
        {
          items: next,
          wallLabels: labels ?? wallLabels ?? DEFAULT_WALL_LABELS,
          widthFt: at.width,
          heightFt: at.height,
          locked: lock ?? locked ?? false,
        },
        { onSuccess: () => setDirty(false) },
      );
    }, 600);
  }

  /**
   * Resize the room, pulling anything now outside the walls back in.
   *
   * Shrinking a room would otherwise strand desks beyond the edge where they
   * can't be reached, so the furniture follows the wall rather than being left
   * behind it.
   */
  function resizeRoom(next: { width: number; height: number }) {
    if (!items) return;
    const bounds = roomUnits(next.width, next.height);
    apply(
      items.map((i) => ({ ...i, ...clampToRoom(i, bounds) })),
      undefined,
      next,
    );
  }

  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  useEffect(() => {
    try {
      localStorage.setItem('td.seatingZoom', String(zoom));
    } catch {
      // Not remembered; the buttons still work.
    }
  }, [zoom]);

  function addItem(kind: RoomItemKind) {
    if (!items) return;
    const spec = ROOM_ITEM_SPECS[kind];
    // Dropped just inside the room rather than dead centre, where it would
    // land on top of whatever is already there.
    const count = items.filter((i) => i.kind === kind).length;
    const item: RoomItem = {
      id: `${kind}-${Date.now().toString(36)}-${count}`,
      kind,
      ...clampToRoom(
        {
          x: snap(ROOM.wall + 20 + (count % 6) * 30),
          y: snap(room.height - spec.h - ROOM.wall - 20 - (count % 4) * 20),
          w: spec.w,
          h: spec.h,
          rotation: 0,
        },
        room,
      ),
      w: spec.w,
      h: spec.h,
      rotation: 0,
      label: null,
      studentId: null,
    };
    apply([...items, item]);
    setSelectedId(item.id);
  }

  function updateItem(id: string, patch: Partial<RoomItem>) {
    if (!items) return;
    const next = items.map((i) => {
      if (i.id !== id) return i;
      const merged = { ...i, ...patch };
      return { ...merged, ...clampToRoom(merged, room) };
    });

    undoRef.current = items;
    apply(next);
    announceNewViolations(items, next);
  }

  function removeItem(id: string) {
    if (!items) return;
    apply(items.filter((i) => i.id !== id));
    setSelectedId(null);
  }

  /**
   * Drop a student on a seat.
   *
   * If the seat is taken and the child came from another seat, the two swap —
   * which is what a teacher means when they drag one child onto another. If
   * they came from the unseated list, the sitting child is displaced back to
   * that list rather than vanishing.
   */
  function seat(itemId: string, studentId: string, fromItemId: string | null) {
    if (!items) return;
    const target = items.find((i) => i.id === itemId);
    if (!target || target.studentId === studentId) return;

    const displaced = target.studentId;

    const next = items.map((i) => {
      if (i.id === itemId) return { ...i, studentId };
      // The seat they came from takes whoever was in the target seat.
      if (fromItemId && i.id === fromItemId) return { ...i, studentId: displaced };
      // Dragged from the list: make sure they aren't left behind anywhere.
      if (!fromItemId && i.studentId === studentId) return { ...i, studentId: null };
      return i;
    });

    undoRef.current = items;
    apply(next);
    announceNewViolations(items, next);
  }

  /**
   * Say something when a change breaks a rule that wasn't broken before.
   *
   * Only *new* breakages are announced. A chart that is already out of order —
   * because the room was rearranged, say — shouldn't shout on every unrelated
   * move, or the warning becomes background noise and stops working.
   */
  function announceNewViolations(before: RoomItem[], after: RoomItem[]) {
    const was = new Set(findViolations(rules, before, nameOfRef.current).map((v) => v.ruleId));
    const fresh = findViolations(rules, after, nameOfRef.current).filter((v) => !was.has(v.ruleId));

    if (fresh.length === 0) return;
    sound.play();
    setAlerting(fresh);
  }

  /** Take a child out of their seat without touching the furniture. */
  function unseat(studentId: string) {
    if (!items) return;
    apply(items.map((i) => (i.studentId === studentId ? { ...i, studentId: null } : i)));
  }

  function clearAllSeats() {
    if (!items) return;
    apply(items.map((i) => ({ ...i, studentId: null })));
  }

  /**
   * Fill empty desks with whoever isn't sitting yet, respecting the rules.
   *
   * Anyone the rules leave nowhere to go is reported rather than seated
   * somewhere forbidden — better to hand back "I couldn't place Billy" than to
   * quietly break the rule the teacher wrote down.
   */
  async function fillSeats() {
    if (!items) return;
    const queue = [...unseated]
      .sort((a, b) => (a.number ?? 999) - (b.number ?? 999))
      .map((s) => s.id);

    const { seating, unplaced } = planSeating(queue, items, rules);

    apply(
      items.map((i) => {
        const who = Object.entries(seating).find(([, seatId]) => seatId === i.id)?.[0];
        return who ? { ...i, studentId: who } : i;
      }),
    );

    if (unplaced.length > 0) {
      const names = unplaced
        .map((id) => roster.find((s) => s.id === id))
        .filter(Boolean)
        .map((s) => studentName(s!))
        .join(', ');

      await confirm({
        title: `Nowhere left for ${unplaced.length === 1 ? names : `${unplaced.length} students`}`,
        body:
          unplaced.length === 1
            ? `Every free seat breaks one of your rules for ${names}. Add a desk, move one, or mute the rule that is in the way.`
            : `Every free seat breaks a rule for ${names}. Add desks, move some, or mute the rules in the way.`,
        confirmLabel: 'I see',
        cancelLabel: 'Close',
      });
    }
  }

  if (error) return <p className="text-sm text-rust">Could not load the seating chart.</p>;
  if (!items || !wallLabels || !feet || locked === null || isLoading)
    return <p className="text-sm text-stone">Loading…</p>;

  const perStudent = squareFeetPerStudent(size.width, size.height, seated.size);

  const violations = findViolations(rules, items, nameOfRef.current);

  return (
    <div className="mx-auto max-w-[1600px]">
      <header className="page-header flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div className="flex items-baseline gap-2">
          <h1 className="text-2xl">Seating chart</h1>
          {/*
            The measurements and the seat count went: they describe a room
            that is drawn full size directly below them. A broken rule and an
            unsaved change do not show anywhere else, so those stay.
          */}
          {violations.length > 0 && (
            <span className="text-xs text-rust">
              {violations.length} rule{violations.length === 1 ? '' : 's'} broken
            </span>
          )}
          {dirty && <span className="text-xs text-stone">saving…</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1" role="group" aria-label="Zoom">
            <button
              onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))}
              disabled={zoom <= 1}
              className="btn-soft px-2 py-1.5 text-sm"
              aria-label="Zoom out"
              title="Zoom out"
            >
              −
            </button>
            <button
              onClick={() => setZoom(1)}
              className="btn-soft min-w-[52px] py-1.5"
              title="Fit the whole room on screen"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))}
              disabled={zoom >= 3}
              className="btn-soft px-2 py-1.5 text-sm"
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
          </div>

          <button
            onClick={() => apply(items, undefined, undefined, !locked)}
            aria-pressed={locked}
            className={locked ? 'btn-primary' : 'btn-secondary'}
            title={
              locked
                ? 'Furniture is pinned. Students can still be moved.'
                : 'Furniture can be dragged around'
            }
          >
            {locked ? 'Room locked' : 'Arranging'}
          </button>
          <button onClick={toggle} aria-pressed={privacy} className="btn-secondary">
            {privacy ? 'Privacy on' : 'Privacy'}
          </button>
          <button onClick={() => window.print()} className="btn-secondary">
            Print
          </button>
        </div>
      </header>

      <div className="mb-3 hidden print:block">
        <h1 className="text-xl">{chart?.name ?? 'Classroom'}</h1>
      </div>

      {/*
        Students on the left, the palette on the right, the room in the middle
        and as wide as the screen allows. The unseated list is the one that
        grows with a real class, so it gets its own column and scrolls in
        place rather than pushing everything else down the page.
      */}
      <div className="grid gap-4 xl:grid-cols-[210px_minmax(0,1fr)_215px] print:block">
        <aside className="order-2 space-y-3 xl:order-1 print:hidden">
          <CollapsiblePanel
            title="Not seated"
            storageKey="seating.unseated"
            badge={unseated.length}
            className={[
              'transition-colors xl:sticky xl:top-4',
              overTray ? 'border-terracotta bg-linen/60' : '',
            ].join(' ')}
            onDragOver={(e) => {
              // Dropping here is how a child leaves a seat.
              if (e.dataTransfer.types.includes('text/from-item')) {
                e.preventDefault();
                setOverTray(true);
              }
            }}
            onDragLeave={() => setOverTray(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOverTray(false);
              const studentId = e.dataTransfer.getData('text/student-id');
              if (studentId) unseat(studentId);
            }}
          >
            {roster.length === 0 ? (
              <p className="text-xs text-stone">No students on the roster yet.</p>
            ) : unseated.length === 0 ? (
              <p className="text-xs text-stone">Everyone has a seat.</p>
            ) : (
              <ul className="max-h-[50vh] space-y-1 overflow-y-auto pr-0.5">
                {unseated.map((s) => (
                  <li key={s.id}>
                    <div
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/student-id', s.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      className="flex cursor-grab items-center gap-2 rounded-lg border border-sand px-2 py-1 text-xs active:cursor-grabbing"
                    >
                      <StudentAvatar student={s} size={22} />
                      <span className="truncate text-espresso">
                        {privacy ? `${studentName(s)[0]}.` : studentName(s)}
                      </span>
                      {s.number != null && (
                        <span className="ml-auto text-[10px] text-stone">{s.number}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <p className="mt-2 text-[11px] leading-snug text-stone">
              Drag a name onto a desk, from one desk to another to swap, or back here to free the
              seat.
            </p>

            <button
              onClick={() => void fillSeats()}
              disabled={unseated.length === 0}
              className="btn-soft-accent mt-2 w-full"
            >
              Fill empty seats
            </button>
          </CollapsiblePanel>
          <CollapsiblePanel
            title="Rules"
            storageKey="seating.rules"
            badge={
              violations.length > 0 ? (
                <span className="text-rust">{violations.length} broken</span>
              ) : (
                rules.length || undefined
              )
            }
          >
            <SeatingRules
              rules={rules}
              students={roster}
              items={items}
              violations={violations}
              onHighlight={setHighlight}
            />
          </CollapsiblePanel>
        </aside>

        <div className="order-1 min-w-0 xl:order-2">
          <RuleBanner
            violations={violations}
            onHighlight={setHighlight}
            soundOn={sound.enabled}
            onToggleSound={sound.toggle}
          />

          <RoomCanvas
            items={items}
            room={room}
            zoom={zoom}
            wallLabels={wallLabels}
            students={roster}
            selectedId={selectedId}
            editing={!locked}
            onSelect={setSelectedId}
            onMove={(id, x, y) => updateItem(id, { x, y })}
            onDropStudent={seat}
            flagged={highlight ?? violations.flatMap((v) => v.itemIds)}
            onWallLabel={(wall, label) =>
              apply(items, { ...wallLabels, [wall]: label || DEFAULT_WALL_LABELS[wall] })
            }
          />

          {selected && (
            <ItemInspector
              item={selected}
              students={roster}
              seatedIds={seated}
              locked={locked}
              onChange={(patch) => updateItem(selected.id, patch)}
              onUnseat={() => updateItem(selected.id, { studentId: null })}
              onRemove={async () => {
                const ok = await confirm({
                  title: `Remove this ${ROOM_ITEM_SPECS[selected.kind].label.toLowerCase()}?`,
                  body: selected.studentId
                    ? 'The furniture goes; the student simply returns to the unseated list.'
                    : 'It goes back to the palette — you can add another at any time.',
                  confirmLabel: 'Remove',
                  danger: true,
                });
                if (ok) removeItem(selected.id);
              }}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>

        <aside className="order-3 space-y-3 print:hidden">
          <CollapsiblePanel
            title="Room size"
            storageKey="seating.size"
            badge={`${size.width}×${size.height} ft`}
          >
            <div className="flex items-end gap-2">
              <label className="flex-1">
                <span className="label mb-1 text-xs">Width (ft)</span>
                <input
                  className="field py-1.5"
                  type="number"
                  min={ROOM_FEET_LIMITS.min}
                  max={ROOM_FEET_LIMITS.max}
                  value={size.width}
                  onChange={(e) =>
                    resizeRoom({ ...size, width: clampRoomFeet(Number(e.target.value)) })
                  }
                />
              </label>
              <span className="pb-2 text-xs text-stone">×</span>
              <label className="flex-1">
                <span className="label mb-1 text-xs">Depth (ft)</span>
                <input
                  className="field py-1.5"
                  type="number"
                  min={ROOM_FEET_LIMITS.min}
                  max={ROOM_FEET_LIMITS.max}
                  value={size.height}
                  onChange={(e) =>
                    resizeRoom({ ...size, height: clampRoomFeet(Number(e.target.value)) })
                  }
                />
              </label>
            </div>

            <p className="mt-2 text-[11px] leading-snug text-stone">
              {roomSizeLabel(size.width, size.height)}
              {perStudent != null && (
                <>
                  <br />
                  About {perStudent} sq ft per seated child.
                </>
              )}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-stone/80">
              Pace the room heel to toe if you don't know — a step is roughly a foot. Furniture is
              drawn to the same scale, so a desk is about 27 inches across.
            </p>
          </CollapsiblePanel>

          <CollapsiblePanel title="Add to the room" storageKey="seating.palette">
            {locked && (
              <p className="mb-2 rounded-lg bg-linen px-2 py-1.5 text-[11px] leading-snug text-walnut">
                The room is locked. Unlock it at the top to add or move furniture.
              </p>
            )}
            {(['seating', 'fixtures', 'furniture'] as const).map((group) => (
              <div key={group} className="mb-3 last:mb-0">
                <p className="label mb-1 text-xs">{GROUP_LABELS[group]}</p>
                <div className="flex flex-wrap gap-1">
                  {ROOM_ITEM_KINDS.filter((k) => ROOM_ITEM_SPECS[k].group === group).map((kind) => (
                    <button
                      key={kind}
                      onClick={() => addItem(kind)}
                      className="rounded-lg border px-1.5 py-0.5 text-[11px] transition-colors hover:bg-linen"
                      style={{
                        borderColor: `${ROOM_ITEM_SPECS[kind].color}66`,
                        color: ROOM_ITEM_SPECS[kind].color,
                      }}
                    >
                      + {ROOM_ITEM_SPECS[kind].label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CollapsiblePanel>

          <CollapsiblePanel title="Start over" storageKey="seating.reset" defaultOpen={false}>
            <p className="mb-2 text-[11px] leading-snug text-stone">
              Empties every seat and leaves the room exactly as you arranged it.
            </p>
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: 'Empty every seat?',
                  body: 'Your layout is untouched; only the children come off the desks.',
                  confirmLabel: 'Empty the seats',
                });
                if (ok) clearAllSeats();
              }}
              className="btn-secondary w-full px-2 py-1.5 text-xs"
              disabled={seated.size === 0}
            >
              Clear the students
            </button>

            <p className="mt-3 border-t border-sand pt-3 text-[11px] leading-snug text-stone">
              Or throw the room away and start from 25 desks in rows:
            </p>
            <button
              onClick={async () => {
                const ok = await confirm({
                  title: 'Replace the whole room?',
                  body: 'Everything you have arranged is discarded and replaced by the starting layout of 25 desks, a board and a door. Seating goes too.',
                  confirmLabel: 'Replace the layout',
                  danger: true,
                });
                if (ok)
                  reset.mutate(undefined, {
                    onSuccess: (fresh) => {
                      setItems(fresh.items);
                      setWallLabels(fresh.wallLabels);
                      setSelectedId(null);
                    },
                  });
              }}
              className="btn-soft-danger mt-1 w-full"
            >
              Replace the layout
            </button>
          </CollapsiblePanel>
        </aside>
      </div>

      {alerting && (
        <RuleAlert
          violations={alerting}
          onUndo={
            undoRef.current
              ? () => {
                  apply(undoRef.current!);
                  undoRef.current = null;
                }
              : null
          }
          onDismiss={() => setAlerting(null)}
        />
      )}
    </div>
  );
}

function ItemInspector({
  item,
  students,
  seatedIds,
  locked,
  onChange,
  onUnseat,
  onRemove,
  onClose,
}: {
  item: RoomItem;
  students: Student[];
  seatedIds: Set<string>;
  /** While locked the seat can be filled, but the furniture cannot change. */
  locked: boolean;
  onChange: (patch: Partial<RoomItem>) => void;
  onUnseat: () => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const spec = ROOM_ITEM_SPECS[item.kind];
  const seat = isSeat(item);
  const sitting = students.find((s) => s.id === item.studentId) ?? null;

  return (
    <div className="card mt-3 print:hidden">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm">{itemLabel(item)}</h2>
          <p className="text-[11px] text-stone">{spec.label}</p>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-stone hover:text-espresso">
          ✕
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {!locked && (
          <>
            <div>
              <label className="label" htmlFor="item-label">
                Name
              </label>
              <input
                id="item-label"
                className="field w-44 py-1.5"
                value={item.label ?? ''}
                maxLength={40}
                placeholder={spec.label}
                onChange={(e) => onChange({ label: e.target.value || null })}
              />
            </div>

            <div>
              <span className="label">Size</span>
              <div className="flex items-center gap-1">
                <input
                  className="field w-16 py-1.5"
                  type="number"
                  min={20}
                  max={ROOM.width}
                  step={10}
                  value={item.w}
                  aria-label="Width"
                  onChange={(e) => onChange({ w: snap(Number(e.target.value) || spec.w) })}
                />
                <span className="text-xs text-stone">×</span>
                <input
                  className="field w-16 py-1.5"
                  type="number"
                  min={20}
                  max={ROOM.height}
                  step={10}
                  value={item.h}
                  aria-label="Height"
                  onChange={(e) => onChange({ h: snap(Number(e.target.value) || spec.h) })}
                />
              </div>
            </div>

            <button
              onClick={() =>
                onChange({ rotation: ((item.rotation + 90) % 360) as RoomItem['rotation'] })
              }
              className="btn-secondary px-3 py-1.5 text-xs"
            >
              Rotate
            </button>
          </>
        )}

        {seat && (
          <div>
            <label className="label" htmlFor="seat-student">
              Who sits here
            </label>
            <select
              id="seat-student"
              className="field w-48 py-1.5"
              value={item.studentId ?? ''}
              onChange={(e) => onChange({ studentId: e.target.value || null })}
            >
              <option value="">Empty</option>
              {students.map((s) => (
                <option
                  key={s.id}
                  value={s.id}
                  disabled={seatedIds.has(s.id) && s.id !== item.studentId}
                >
                  {s.number != null ? `${s.number}. ` : ''}
                  {studentName(s)} {s.lastName}
                  {seatedIds.has(s.id) && s.id !== item.studentId ? ' — already seated' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {sitting && (
            <button onClick={onUnseat} className="btn-secondary px-3 py-1.5 text-xs">
              Empty this seat
            </button>
          )}
          {locked ? (
            <span className="text-xs text-stone">Room locked</span>
          ) : (
            <button onClick={onRemove} className="btn-danger px-3 py-1.5 text-xs">
              Remove the {spec.label.toLowerCase()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
